import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { Query, useQueryClient } from 'react-query';
import { BulkMessagingBaseKey } from '../queries';

// By convention, the 3rd element in the query key is the request object
const QUERY_KEY_REQUEST_INDEX = 2;

/**
 * Extracts the request object from a Bulk Messaging query.
 *
 * By convention, the request object is stored as the 3rd element in Bulk Messaging query key arrays.
 * This function safely retrieves that request object with optional type validation.
 *
 * @template T - The expected type of the request object
 * @param query - The React Query object containing the queryKey
 * @param validator - Optional type guard function to validate the request object
 * @returns The request object cast to type T, or undefined if not found or validation fails
 */
export const getQueryRequest = <T>({ queryKey }: Query, validator?: (request: unknown) => request is T) => {
  if (queryKey.length <= QUERY_KEY_REQUEST_INDEX) {
    return undefined;
  }

  const request = queryKey[QUERY_KEY_REQUEST_INDEX];

  if (validator && !validator(request)) {
    return undefined;
  }

  return request as T;
};

export type CampaignObject = { campaignId: string };
export type TypeObject = { type?: CampaignType; types?: CampaignType[] };

/**
 * Type guard to check if an object has a valid campaignId property.
 *
 * @param obj - The object to check
 * @returns True if the object has a populated campaignId string, false otherwise
 */
export const isCampaignObject = (obj: any): obj is CampaignObject =>
  obj && typeof obj === 'object' && typeof obj.campaignId === 'string' && obj.campaignId.trim() !== '';

/**
 * Type guard to check if an object has a valid type or types property.
 *
 * @param obj - The object to check
 * @returns True if the object has a populated type enum or populated types array, false otherwise
 */
export const isTypeObject = (obj: any): obj is TypeObject => {
  if (!obj || typeof obj !== 'object') return false;

  const hasValidTypes = !!(obj.types && Array.isArray(obj.types) && obj.types.length > 0);
  const hasValidType = !!(obj.type && Object.values(CampaignType).includes(obj.type));

  return hasValidTypes || hasValidType;
};

/**
 * Creates a predicate function for filtering queries by campaignId.
 * Used with React Query's removeQueries, invalidateQueries, cancelQueries, etc. methods.
 *
 * @param campaignId - The campaign ID to match against
 * @returns An object with a predicate function that matches queries with the given campaignId
 */
export const createCampaignPredicate = (campaignId: string) => {
  return {
    predicate: (query: Query) => {
      const requestObject = getQueryRequest<CampaignObject>(query, isCampaignObject);
      return requestObject?.campaignId === campaignId;
    },
  };
};

/**
 * Creates a predicate function for filtering queries by campaignType.
 * Used with React Query's removeQueries, invalidateQueries, cancelQueries, etc. methods.
 *
 * @param campaignTypes - Array of campaign types to match against
 * @returns An object with a predicate function that matches queries with the given campaignType
 */
export const createTypePredicate = (campaignTypes: CampaignType[]) => {
  return {
    predicate: (query: Query) => {
      const requestObject = getQueryRequest<TypeObject>(query, isTypeObject);

      const matchesType = !!(requestObject?.type && campaignTypes.includes(requestObject.type));
      const matchesTypes = !!(requestObject?.types && requestObject.types.some((type) => campaignTypes.includes(type)));

      return matchesType || matchesTypes;
    },
  };
};

/**
 * Provides utility functions for managing Bulk Messaging React Query caches.
 *
 * @returns An object containing query management utilities
 */
export const useBulkMessagingQueryHelpers = () => {
  const queryClient = useQueryClient();

  return {
    queryClient,
    /**
     * Cancels all in-flight queries for the specified keys that match the given campaignId.
     * NOTE: Passed keys must be base keys that correspond with request objects having a campaignId. Request objects without a campaignId are ignored.
     *
     * @param keys - Array of base query keys to cancel
     * @param campaignId - The campaign ID to match against
     */
    cancelQueriesByCampaign: (keys: BulkMessagingBaseKey[], campaignId: string) => {
      keys.forEach((key) => {
        queryClient.cancelQueries(key, createCampaignPredicate(campaignId));
      });
    },

    /**
     * Cancels all in-flight queries for the specified keys that match at least one of the given campaignTypes.
     * NOTE: Passed keys must be base keys that correspond with request objects having a type or types. Request objects without a type or types property are ignored.
     *
     * @param keys - Array of base query keys to cancel
     * @param campaignTypes - The campaign types to match against
     */
    cancelQueriesByType: (keys: BulkMessagingBaseKey[], campaignTypes: CampaignType[]) => {
      keys.forEach((key) => {
        queryClient.cancelQueries(key, createTypePredicate(campaignTypes));
      });
    },

    /**
     * Invalidates all cached queries for the specified keys that match the given campaignId.
     * NOTE: Passed keys must be base keys that correspond with request objects having a campaignId. Request objects without a campaignId are ignored.
     *
     * @param keys - Array of base query keys to invalidate
     * @param campaignId - The campaign ID to match against
     */
    invalidateQueriesByCampaign: (keys: BulkMessagingBaseKey[], campaignId: string) => {
      keys.forEach((key) => {
        queryClient.invalidateQueries(key, createCampaignPredicate(campaignId));
      });
    },

    /**
     * Invalidates all cached queries for the specified keys that match at least one of the given campaignTypes.
     * NOTE: Passed keys must be base keys that correspond with request objects having a type or types. Request objects without a type or types property are ignored.
     *
     * @param keys - Array of base query keys to invalidate
     * @param campaignTypes - The campaign types to match against
     */
    invalidateQueriesByType: (keys: BulkMessagingBaseKey[], campaignTypes: CampaignType[]) => {
      keys.forEach((key) => {
        queryClient.invalidateQueries(key, createTypePredicate(campaignTypes));
      });
    },

    /**
     * Removes all cached queries for the specified keys that match the given campaignId.
     * NOTE: Passed keys must be base keys that correspond with request objects having a campaignId. Request objects without a campaignId are ignored.
     *
     * @param keys - Array of base query keys to remove
     * @param campaignId - The campaign ID to match against
     */
    removeQueriesByCampaign: (keys: BulkMessagingBaseKey[], campaignId: string) => {
      keys.forEach((key) => {
        queryClient.removeQueries(key, createCampaignPredicate(campaignId));
      });
    },

    /**
     * Removes all cached queries for the specified keys that match at least one of the given campaignTypes.
     * NOTE: Passed keys must be base keys that correspond with request objects having a type or types. Request objects without a type or types property are ignored.
     *
     * @param keys - Array of base query keys to remove
     * @param campaignTypes - The campaign types to match against
     */
    removeQueriesByType: (keys: BulkMessagingBaseKey[], campaignTypes: CampaignType[]) => {
      keys.forEach((key) => {
        queryClient.removeQueries(key, createTypePredicate(campaignTypes));
      });
    },

    /**
     * Finds the first query key that matches the given base key and optional validator.
     *
     * @template T - The expected type of the request object for validation
     * @param key - The base query key to search for
     * @param validator - Optional type guard function to validate the request object type
     * @param campaignType - Optional campaign type to filter by. If provided, only queries with a matching campaign type will be considered
     * @param orgId - Optional orgId to filter by. If provided, only queries including orgId will be considered. This helps narrow down the relevant queries since we could be going across organizations.
     * @returns The query key of the first matching query, or undefined if no matches found
     */
    findFirstQueryKey: <T>({
      campaignType,
      key,
      orgId,
      validator,
    }: {
      campaignType?: CampaignType;
      key: BulkMessagingBaseKey;
      orgId?: string;
      validator?: (request: unknown) => request is T;
    }) => {
      const queryCache = queryClient.getQueryCache();
      let matchedQueries = queryCache
        .findAll(key)
        .filter((query) => getQueryRequest<T>(query, validator) !== undefined);

      // Filter by campaign type if provided
      if (campaignType !== undefined) {
        matchedQueries = matchedQueries.filter((query) => {
          const requestObject = getQueryRequest<TypeObject>(query, isTypeObject);
          if (!requestObject) return false;
          const queryOrgId = getQueryRequest<{ orgId?: string }>(query);
          const includesOrgId = !orgId || queryOrgId?.orgId === orgId;
          return (requestObject.type === campaignType || requestObject.types?.includes(campaignType)) && includesOrgId;
        });
      }

      return matchedQueries[0]?.queryKey;
    },
  };
};
